import { Injectable } from '@angular/core';
import { MetadataItem, MetadataItemValue } from '../types/metadata-item';
import { isNil, pick } from 'ramda';

@Injectable({ providedIn: 'root' })
export class MetadataService {
  getItem(metadata: MetadataItem[], key: string, skipEmptyValues?: boolean): MetadataItem {
    return (metadata || []).find((loopMetadataItem: MetadataItem): boolean => {
      if (loopMetadataItem.key !== key) {
        return false;
      }

      if (skipEmptyValues && !loopMetadataItem.value) {
        return false;
      }

      return true;
    });
  }

  getItemValue(metadata: MetadataItem[], key: string): MetadataItemValue {
    return this.getItem(metadata, key)?.value;
  }

  getParsedJsonItemValue(metadata: MetadataItem[], key: string, defaultValue: unknown = null): unknown {
    const value: string = this.getItemValue(metadata, key) as string;

    if (!value) {
      return defaultValue;
    }

    try {
      return JSON.parse(value);
    } catch (e) {
      return defaultValue;
    }
  }

  getBooleanItemValue(metadata: MetadataItem[], key: string, defaultValue: boolean = false): boolean {
    return Boolean(this.getParsedJsonItemValue(metadata, key));
  }

  updateItem(metadata: MetadataItem[], key: string, value: MetadataItemValue): MetadataItem[] {
    if (!value) {
      return this.removeItem(metadata, key);
    }

    if (this.getItem(metadata, key)) {
      return (metadata || []).map((loopItem: MetadataItem) => {
        if (loopItem.key !== key) {
          return loopItem;
        }

        return { ...loopItem, value };
      });
    }

    return this.addItem(metadata, key, value);
  }

  updateItems(existingItems: MetadataItem[], itemsToUpdate: MetadataItem[]): MetadataItem[] {
    let metadata = existingItems && existingItems.length ? [...existingItems] : [];

    if (!itemsToUpdate || !itemsToUpdate.length) {
      return metadata;
    }

    itemsToUpdate.forEach((loopMetadataItem: MetadataItem) => {
      metadata = this.updateItem(metadata, loopMetadataItem.key, loopMetadataItem.value);
    });

    return metadata;
  }

  updateJsonItemValue(metadataItems: MetadataItem[], key: string, value: unknown): MetadataItem[] {
    const valueStringified = !isNil(value) ? JSON.stringify(value) : null;

    return this.updateItem(metadataItems, key, valueStringified);
  }

  updateBooleanItemValue(metadataItems: MetadataItem[], key: string, value: boolean): MetadataItem[] {
    return this.updateJsonItemValue(metadataItems, key, value);
  }

  // Helper cleaning up metadata from ID fields: "id", "productId", "variantId", "imageId"
  // Keeps essential fields only: "key" and "value"
  normalize(metadata: MetadataItem[]): MetadataItem[] {
    if (!metadata?.length) {
      return [];
    }

    return metadata.map((loopMetadataItem: MetadataItem): MetadataItem => this.normalizeItem(loopMetadataItem));
  }

  private normalizeItem(metadataItem: MetadataItem): MetadataItem {
    return pick(['key', 'value'], metadataItem);
  }

  private addItem(metadata: MetadataItem[], key: string, value: MetadataItemValue): MetadataItem[] {
    if (!metadata || !metadata.length) {
      return [{ key, value }];
    }

    return [...metadata, { key, value }];
  }

  private removeItem(metadata: MetadataItem[], key: string): MetadataItem[] {
    if (!metadata || !metadata.length) {
      return [];
    }

    return metadata.filter((loopItem: MetadataItem): boolean => loopItem.key !== key);
  }
}
